import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, CalendarCheck, ChevronRight, Award, ShieldCheck, Leaf, BadgeCheck, Star, ExternalLink } from "lucide-react";
import { BUSINESS, TRUST_CHIPS, GOOGLE_REVIEWS_URL, GOOGLE_REVIEW_WRITE_URL } from "@/data/site";
import { trackCall } from "@/lib/analytics";

export function Reveal({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.45, ease: "easeOut", delay }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function SectionHeading({ kicker, title, sub, center = false, dark = false }) {
    return (
        <Reveal className={`max-w-2xl ${center ? "mx-auto text-center" : ""} mb-7 sm:mb-10`}>
            {kicker && (
                <p className={`font-mono-brand text-xs font-bold uppercase tracking-[0.2em] mb-2.5 ${dark ? "text-amber-400" : "text-red-600"}`}>{kicker}</p>
            )}
            <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
            {sub && <p className={`mt-3 text-base sm:text-lg leading-relaxed ${dark ? "text-slate-200" : "text-slate-600"}`}>{sub}</p>}
        </Reveal>
    );
}

export function CallButton({ location, className = "", children, testid = "call-btn" }) {
    return (
        <a href={BUSINESS.tel} onClick={() => trackCall(location)}
            className={`inline-flex items-center justify-center gap-2.5 bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3.5 rounded-xl shadow-lg shadow-red-600/25 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 ${className}`}
            data-testid={testid}>
            <Phone className="h-5 w-5" />
            <span>{children || `Call ${BUSINESS.phone}`}</span>
        </a>
    );
}

export function RequestButton({ to = "/request-service", className = "", children, testid = "request-btn" }) {
    return (
        <Link to={to}
            className={`inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-6 py-3.5 rounded-xl shadow-md transition-all duration-200 hover:-translate-y-0.5 ${className}`}
            data-testid={testid}>
            <CalendarCheck className="h-5 w-5" />
            <span>{children || "Request Service"}</span>
        </Link>
    );
}

export function TrustChips({ dark = false }) {
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="trust-chips">
            {TRUST_CHIPS.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.06}>
                    <div className={`flex items-start gap-3 rounded-xl border p-3.5 h-full ${dark ? "border-slate-700 bg-slate-800/60" : "border-slate-200 bg-white shadow-sm"}`}>
                        <c.icon className={`h-5 w-5 mt-0.5 shrink-0 ${dark ? "text-amber-400" : "text-red-600"}`} />
                        <div>
                            <p className={`font-heading font-bold text-sm leading-tight ${dark ? "text-white" : "text-slate-900"}`}>{c.title}</p>
                            <p className={`text-xs mt-0.5 ${dark ? "text-slate-300" : "text-slate-500"}`}>{c.subtitle}</p>
                        </div>
                    </div>
                </Reveal>
            ))}
        </div>
    );
}

const TRUST_STRIP_ITEMS = [
    { icon: Award, label: "4 Years Experience" },
    { icon: ShieldCheck, label: "Insured" },
    { icon: Leaf, label: "EPA Certified" },
    { icon: BadgeCheck, label: "6-Month Repair Warranty" },
];

export function TrustStrip() {
    return (
        <div className="bg-slate-950 border-y border-slate-800" data-testid="trust-strip">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
                {TRUST_STRIP_ITEMS.map((t) => (
                    <span key={t.label} className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-200">
                        <t.icon className="h-4 w-4 text-amber-400" /> {t.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

export function Breadcrumbs({ items }) {
    return (
        <nav aria-label="Breadcrumb" className="mb-5" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1 text-sm text-slate-400">
                {items.map((it, i) => (
                    <li key={it.path} className="flex items-center gap-1">
                        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                        {i === items.length - 1 ? (
                            <span className="text-slate-200 font-medium" aria-current="page">{it.name}</span>
                        ) : (
                            <Link to={it.path} className="hover:text-amber-400 transition-colors">{it.name}</Link>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export function GoogleReviewsBlock({ center = true }) {
    return (
        <div className={center ? "text-center" : ""} data-testid="google-reviews-block">
            <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-3">Google reviews</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">See What Our Customers Say</h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto leading-relaxed">
                Read genuine feedback from Appliance Mania customers on Google — real reviews from real
                in-home repairs across Brevard &amp; Seminole Counties.
            </p>
            <div className={`mt-6 flex flex-col sm:flex-row gap-3 ${center ? "justify-center" : ""}`}>
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border-2 border-slate-900 bg-white text-slate-900 font-bold px-6 py-3.5 rounded-xl transition-all hover:bg-slate-900 hover:text-white"
                    data-testid="read-google-reviews-btn">
                    <Star className="h-5 w-5" /> Read Our Google Reviews <ExternalLink className="h-4 w-4" />
                </a>
                <a href={GOOGLE_REVIEW_WRITE_URL} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:text-red-600 px-4 py-3.5 transition-colors"
                    data-testid="leave-google-review-btn">
                    Leave Us a Google Review <ExternalLink className="h-3.5 w-3.5" />
                </a>
            </div>
        </div>
    );
}

export function CtaBand({ title, sub, testid = "cta-band" }) {
    return (
        <section className="bg-slate-950 dark-grain" data-testid={testid}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
                <Reveal>
                    <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl mx-auto">{title}</h2>
                    {sub && <p className="mt-3 text-slate-200 max-w-xl mx-auto">{sub}</p>}
                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <CallButton location={`${testid}-call`} testid={`${testid}-call-btn`} />
                        <RequestButton testid={`${testid}-request-btn`} />
                    </div>
                    <p className="mt-4 text-xs text-slate-400">After-hours emergency service may be available from 10 PM – 6 AM — call <a href={BUSINESS.tel} onClick={() => trackCall(`${testid}-after-hours`)} className="text-slate-300 font-semibold underline underline-offset-2">{BUSINESS.phone}</a> for availability.</p>
                </Reveal>
            </div>
        </section>
    );
}
